/**
 * The rewarded-ad path that only exists inside the ShweTap app.
 *
 * AdMob has no web rewarded product. On a browser, `/ads/complete` answers `AD_NOT_VERIFIED`
 * forever, `canRewardAds()` hides the CTA, and that is correct — see `availability.ts`.
 *
 * Inside the Android/iOS shell the same page is running, but a native AdMob SDK is one
 * `postMessage` away. The shell installs `window.ShweTapNative` before this bundle's first
 * line (`mobile/src/screens/webBridge.ts`), so a single `if` here is the whole difference
 * between a treasure rock that can pay its Kyat and one that cannot.
 *
 * **This module cannot grant anything, and neither can the shell.** The outcome it returns is
 * whatever the *server* answered when the shell polled `/ads/complete` after Google's signed
 * callback — money rule 3. What comes back over the bridge is a report, not a payment; the
 * only thing the page does with it is pass `impressionUuid` to `/cave/claim`, which checks the
 * impression again before crediting a single Kyat.
 */

/** Mirrors `AdOutcome` in `mobile/src/ads/rewarded.ts`. */
import { sound } from '@/game/audio/sound';

export type NativeAdOutcome =
  /** SSV landed and the server verified it. `impressionUuid` is the key to `/cave/claim`. */
  | { status: 'rewarded'; impressionUuid: string; currency: string; amount: string }
  /** Watched, but the callback was still in flight. Claim may still re-check it. */
  | { status: 'unverified'; impressionUuid: string }
  /** Closed early. No reward earned, and not an error. */
  | { status: 'dismissed' }
  /** No fill, no network, or the SDK refused. */
  | { status: 'failed'; reason: string };

/**
 * A key in the server's rewarded-ad catalogue.
 *
 * Not a union on purpose. There are eight seeded placements and the Reward Hub uses six of
 * them; the catalogue is server data, editable in the admin, and the server refuses an
 * unknown key on `/ads/request`. A union written here would be a second copy that goes stale
 * the first time a placement is added — and it would go stale by *hiding* a working offer.
 */
export type AdPlacement = string;

export interface AdRef { type: string; id: string }

interface ShweTapNative {
  platform: 'android' | 'ios';
  appVersion: string;
  bridge: number;
  requestRewardedAd(placement: AdPlacement, ref?: AdRef | null): Promise<unknown>;
}

/** The protocol this build speaks. An older shell is refused rather than guessed at. */
const REQUIRED_BRIDGE = 1;

/**
 * The shell's ad channel, or null in a browser.
 *
 * Read on every call rather than cached at module load: the bundle can be evaluated before
 * the injection on a cold WebView start, and a `null` frozen in at that moment would hide the
 * CTA for the whole session on the one device that can pay it.
 */
export function nativeBridge(): ShweTapNative | null {
  const api = (globalThis as { ShweTapNative?: Partial<ShweTapNative> }).ShweTapNative;
  if (!api || typeof api.requestRewardedAd !== 'function') return null;
  if (api.bridge !== REQUIRED_BRIDGE) return null;
  return api as ShweTapNative;
}

/**
 * How long to wait for the shell before giving up on it.
 *
 * Generous on purpose: the shell's own budget is a 20s load, then the ad itself, then 25s of
 * polling for Google's callback. Timing out under that would abandon an ad the player already
 * watched. This is the backstop for the case the shell cannot answer at all — killed by the
 * OS behind a full-screen ad, most likely — and the player gets a card they can dismiss
 * instead of a spinner that never ends.
 */
const BRIDGE_TIMEOUT_MS = 180_000;

/**
 * Show a rewarded ad through the app shell.
 *
 * Resolves with what the server decided. Never rejects: every caller of this is a UI that has
 * to put *something* on screen, and a thrown error at the end of a 30-second ad is the worst
 * possible moment to ask a component to have an error path.
 */
export async function requestNativeRewardedAd(
  placement: AdPlacement,
  ref?: AdRef,
): Promise<NativeAdOutcome> {
  const api = nativeBridge();
  if (!api) return { status: 'failed', reason: 'Rewarded ads need the ShweTap app.' };

  /* The ad is about to cover the page with its own video, and the cave's drone is a looping
     oscillator that would otherwise play underneath it. Suspending here rather than relying
     on `visibilitychange` because that event is the WebView's to fire and an ad activity does
     not always take the page out of view — and an ad the player cannot hear is one they
     cannot be rewarded for watching. Restored in `finally`, so a crashed or refused ad does
     not leave the game silent. */
  sound.suspend();

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const answered = await Promise.race([
      api.requestRewardedAd(placement, ref ?? null),
      new Promise<'timeout'>((resolve) => {
        timer = setTimeout(() => resolve('timeout'), BRIDGE_TIMEOUT_MS);
      }),
    ]);
    if (answered === 'timeout') {
      return { status: 'failed', reason: 'The ad did not finish in time.' };
    }
    return parseOutcome(answered);
  } catch {
    return { status: 'failed', reason: 'Something went wrong showing the ad.' };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    sound.unsuspend();
  }
}

/**
 * Read the shell's answer defensively.
 *
 * The shell is ours, but it is a *separately released binary* — an install from six months
 * ago is still out there sending whatever that build sent. Anything unrecognised becomes a
 * plain failure rather than a crash inside a loot card the player is looking at.
 */
export function parseOutcome(value: unknown): NativeAdOutcome {
  if (typeof value !== 'object' || value === null) {
    return { status: 'failed', reason: 'The app could not show an ad.' };
  }
  const o = value as Record<string, unknown>;
  const uuid = typeof o.impressionUuid === 'string' ? o.impressionUuid : '';

  if (o.status === 'rewarded' && uuid) {
    return {
      status: 'rewarded',
      impressionUuid: uuid,
      currency: typeof o.currency === 'string' ? o.currency : 'none',
      amount: typeof o.amount === 'string' ? o.amount : '0.00',
    };
  }
  if (o.status === 'unverified' && uuid) return { status: 'unverified', impressionUuid: uuid };
  if (o.status === 'dismissed') return { status: 'dismissed' };
  if (o.status === 'failed') {
    return {
      status: 'failed',
      reason: typeof o.reason === 'string' && o.reason
        ? o.reason
        : 'Something went wrong showing the ad.',
    };
  }
  return { status: 'failed', reason: 'The app could not show an ad.' };
}
